import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faRotate, faSpinner, faTimes, faPlus, faChevronDown, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { watchlistApi } from '../api/watchlistApi';
import { EarningsBadge } from '../contexts/EarningsBookContext';
import { NoteBadge } from '../contexts/SymbolNotesContext';
import { useAssetNames } from '../hooks/useAssetNames';

const WatchlistAlpaca = ({ onSymbolSelect }) => {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMarketData, setLoadingMarketData] = useState(false);
  const [error, setError] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const isLoadingRef = useRef(false);
  const isLoadingMarketDataRef = useRef(false);
  const openSymbol = (symbol, marketInfo = null) => {
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
      return;
    }
    navigate(`/asset/${symbol}`, {
      state: {
        marketData: marketInfo,
        fromWatchlist: true,
      },
    });
  };

  useEffect(() => {
    // Reload watchlists on mount (which happens after page reload when switching env)
    // Clear selected watchlist to force fresh load
    setSelectedWatchlist(null);
    setMarketData([]);
    loadWatchlists();
  }, []);

  useEffect(() => {
    if (selectedWatchlist && !isLoadingMarketDataRef.current) {
      loadMarketData();
      // Auto-refresh removed - use Refresh button to manually update
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWatchlist?.id]); // Only depend on ID to prevent unnecessary calls

  const loadWatchlists = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current) {
      console.log('[WatchlistAlpaca] Already loading watchlists, skipping...');
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      console.log('[WatchlistAlpaca] Loading watchlists...');
      const response = await watchlistApi.getAllWatchlists();
      console.log('[WatchlistAlpaca] Watchlists response:', response);
      const watchlistsData = response.data || response || [];
      console.log('[WatchlistAlpaca] Watchlists data:', watchlistsData);
      setWatchlists(Array.isArray(watchlistsData) ? watchlistsData : []);
      
      // Auto-select first watchlist if none selected - fetch full details
      if (!selectedWatchlist && watchlistsData.length > 0) {
        try {
          console.log('[WatchlistAlpaca] Auto-selecting first watchlist, fetching details:', watchlistsData[0].id);
          const fullWatchlistResponse = await watchlistApi.getWatchlistById(watchlistsData[0].id);
          const fullWatchlist = fullWatchlistResponse.data || fullWatchlistResponse;
          setSelectedWatchlist(fullWatchlist);
        } catch (err) {
          console.error('[WatchlistAlpaca] Error fetching watchlist details for auto-select:', err);
          // Fallback to using the watchlist from the list
          setSelectedWatchlist(watchlistsData[0]);
        }
      }
    } catch (err) {
      console.error('[WatchlistAlpaca] Error loading watchlists:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load watchlists';
      // Don't show error if it's just that no watchlists exist yet (404)
      if (err.response?.status === 404) {
        console.log('[WatchlistAlpaca] No watchlists found (404) - this is normal if none exist yet');
        setError(null);
        setWatchlists([]);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const loadMarketData = async () => {
    if (!selectedWatchlist) return;
    
    // Prevent multiple simultaneous calls
    if (isLoadingMarketDataRef.current) {
      console.log('[WatchlistAlpaca] Already loading market data, skipping...');
      return;
    }
    
    try {
      isLoadingMarketDataRef.current = true;
      setLoadingMarketData(true);
      console.log('[WatchlistAlpaca] Loading market data for watchlist:', selectedWatchlist.id);
      // First fetch the full watchlist details to ensure we have the latest data
      const watchlistResponse = await watchlistApi.getWatchlistById(selectedWatchlist.id);
      console.log('[WatchlistAlpaca] Watchlist details:', watchlistResponse);
      // Update selected watchlist with fresh data, but only if ID matches to prevent loops
      const updatedWatchlist = watchlistResponse.data || watchlistResponse;
      if (updatedWatchlist && updatedWatchlist.id === selectedWatchlist.id) {
        setSelectedWatchlist(updatedWatchlist);
      }
      // Then fetch market data
      const response = await watchlistApi.getWatchlistMarketData(selectedWatchlist.id);
      setMarketData(response.data || []);
      setError(null);
    } catch (err) {
      console.error('[WatchlistAlpaca] Error loading market data:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingMarketData(false);
      isLoadingMarketDataRef.current = false;
    }
  };

  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim()) return;
    
    try {
      setLoading(true);
      await watchlistApi.createWatchlist(newWatchlistName.trim());
      setNewWatchlistName('');
      setShowCreateForm(false);
      await loadWatchlists();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSymbol = async () => {
    if (!selectedWatchlist || !newSymbol.trim()) return;
    
    try {
      setLoading(true);
      await watchlistApi.addSymbol(selectedWatchlist.id, newSymbol.trim().toUpperCase());
      setNewSymbol('');
      await loadMarketData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSymbol = async (symbol) => {
    if (!selectedWatchlist) return;
    
    try {
      setLoading(true);
      await watchlistApi.removeSymbol(selectedWatchlist.id, symbol);
      await loadMarketData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWatchlist = async (watchlistId) => {
    if (!window.confirm('Are you sure you want to delete this watchlist?')) {
      return;
    }
    
    try {
      setLoading(true);
      await watchlistApi.deleteWatchlist(watchlistId);
      if (selectedWatchlist?.id === watchlistId) {
        setSelectedWatchlist(null);
        setMarketData([]);
      }
      await loadWatchlists();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const calculateChange = (current, previous) => {
    if (!current || !previous || isNaN(current) || isNaN(previous)) return null;
    return ((current - previous) / previous) * 100;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faSort} className="text-gray-400 text-[8px] ml-1" />;
    }
    return sortDirection === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="text-indigo-600 text-[8px] ml-1" />
      : <FontAwesomeIcon icon={faSortDown} className="text-indigo-600 text-[8px] ml-1" />;
  };

  const symbols = selectedWatchlist?.assets?.map(asset => 
    typeof asset === 'string' ? asset : asset.symbol
  ) || [];

  const watchlistNameSeed = useMemo(() => {
    const map = {};
    for (const asset of selectedWatchlist?.assets || []) {
      if (asset && typeof asset === 'object' && asset.symbol && asset.name) {
        map[String(asset.symbol).toUpperCase()] = asset.name;
      }
    }
    return map;
  }, [selectedWatchlist]);

  const assetNames = useAssetNames(symbols, watchlistNameSeed);

  // Create sortable data array
  const sortableData = symbols.map(symbol => {
    const data = marketData.find(d => d.symbol === symbol);
    const marketInfo = data?.data;
    const change = marketInfo?.close && marketInfo?.open
      ? marketInfo.close - marketInfo.open
      : null;
    const changePercent = marketInfo?.close && marketInfo?.open
      ? calculateChange(marketInfo.close, marketInfo.open)
      : null;
    
    return {
      symbol,
      data,
      marketInfo,
      change,
      changePercent,
      hasError: data?.error,
    };
  });

  const sortedData = [...sortableData].sort((a, b) => {
    if (!sortColumn) return 0;
    
    // Handle error cases
    if (a.hasError && !b.hasError) return 1;
    if (!a.hasError && b.hasError) return -1;
    if (a.hasError && b.hasError) return 0;
    
    if (!a.marketInfo || !b.marketInfo) {
      if (!a.marketInfo && !b.marketInfo) return 0;
      return !a.marketInfo ? 1 : -1;
    }
    
    let aVal, bVal;
    switch (sortColumn) {
      case 'symbol':
        aVal = a.symbol?.toUpperCase() || '';
        bVal = b.symbol?.toUpperCase() || '';
        break;
      case 'currentPrice':
        aVal = parseFloat(a.marketInfo.currentPrice) || 0;
        bVal = parseFloat(b.marketInfo.currentPrice) || 0;
        break;
      case 'open':
        aVal = parseFloat(a.marketInfo.open) || 0;
        bVal = parseFloat(b.marketInfo.open) || 0;
        break;
      case 'high':
        aVal = parseFloat(a.marketInfo.high) || 0;
        bVal = parseFloat(b.marketInfo.high) || 0;
        break;
      case 'low':
        aVal = parseFloat(a.marketInfo.low) || 0;
        bVal = parseFloat(b.marketInfo.low) || 0;
        break;
      case 'close':
        aVal = parseFloat(a.marketInfo.close) || 0;
        bVal = parseFloat(b.marketInfo.close) || 0;
        break;
      case 'change':
        aVal = a.change || 0;
        bVal = b.change || 0;
        break;
      case 'changePercent':
        aVal = a.changePercent || 0;
        bVal = b.changePercent || 0;
        break;
      case 'volume':
        aVal = parseFloat(a.marketInfo.volume) || 0;
        bVal = parseFloat(b.marketInfo.volume) || 0;
        break;
      default:
        return 0;
    }
    
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-2">
        <h2 className="text-lg font-bold text-gray-800">
          <FontAwesomeIcon icon={faChartLine} className="mr-1.5 text-sm" />
          Watchlists
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1 text-xs" />
            New Watchlist
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isLoadingRef.current && !loading) {
                loadWatchlists();
              }
            }}
            disabled={loading || isLoadingRef.current}
            className="px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin text-xs" />
                Refreshing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRotate} className="mr-1 text-xs" />
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex gap-2">
            <input
              type="text"
              value={newWatchlistName}
              onChange={(e) => setNewWatchlistName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateWatchlist()}
              placeholder="Watchlist name"
              className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleCreateWatchlist}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
            >
              Create
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewWatchlistName('');
              }}
              className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <strong>Error:</strong> {error}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isLoadingRef.current && !loading) {
                loadWatchlists();
              }
            }}
            disabled={loading || isLoadingRef.current}
            className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {loading && watchlists.length === 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
          Loading watchlists...
        </div>
      )}

      {/* Watchlist Dropdown and Content */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 flex-1">
            <label htmlFor="watchlist-select" className="text-xs font-semibold text-gray-700 whitespace-nowrap">
              Select Watchlist:
            </label>
            <div className="flex-1 relative">
              <select
                id="watchlist-select"
                value={selectedWatchlist?.id || ''}
                onChange={async (e) => {
                  const watchlistId = e.target.value;
                  if (!watchlistId || isLoadingRef.current) {
                    return;
                  }
                  // Find the watchlist from the list
                  const watchlist = watchlists.find(w => w.id === watchlistId);
                  if (!watchlist) return;
                  
                  // Fetch full watchlist details using GET /v2/watchlists/{id}
                  try {
                    console.log('[WatchlistAlpaca] Fetching full details for watchlist:', watchlist.id);
                    isLoadingMarketDataRef.current = true; // Prevent useEffect from triggering
                    const fullWatchlistResponse = await watchlistApi.getWatchlistById(watchlist.id);
                    const fullWatchlist = fullWatchlistResponse.data || fullWatchlistResponse;
                    console.log('[WatchlistAlpaca] Full watchlist details:', fullWatchlist);
                    setSelectedWatchlist(fullWatchlist);
                    isLoadingMarketDataRef.current = false;
                  } catch (err) {
                    console.error('[WatchlistAlpaca] Error fetching watchlist details:', err);
                    isLoadingMarketDataRef.current = false;
                    // Fallback to using the watchlist from the list if fetch fails
                    setSelectedWatchlist(watchlist);
                  }
                }}
                disabled={loading || watchlists.length === 0}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-gray-800 font-medium appearance-none pr-8"
              >
                {watchlists.length === 0 ? (
                  <option value="">{loading ? 'Loading watchlists...' : 'No watchlists found'}</option>
                ) : (
                  <>
                    <option value="">Select a watchlist...</option>
                    {watchlists.map((watchlist) => (
                      <option key={watchlist.id} value={watchlist.id}>
                        {watchlist.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs" />
              </div>
            </div>
            {selectedWatchlist && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Are you sure you want to delete "${selectedWatchlist.name}"?`)) {
                    handleDeleteWatchlist(selectedWatchlist.id);
                  }
                }}
                className="px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors text-xs font-medium"
                title="Delete watchlist"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            )}
          </div>
        </div>

        {/* Watchlist Content */}
        <div>
          {!selectedWatchlist ? (
            <div className="text-center py-12 text-gray-500">
              Select a watchlist to view symbols
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  {selectedWatchlist.name}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isLoadingMarketDataRef.current && !loadingMarketData) {
                        loadMarketData();
                      }
                    }}
                    disabled={loadingMarketData || isLoadingMarketDataRef.current}
                    className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                    title="Refresh market data"
                  >
                    {loadingMarketData ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                    ) : (
                      <FontAwesomeIcon icon={faRotate} className="text-xs" />
                    )}
                  </button>
                  <input
                    type="text"
                    value={newSymbol}
                    onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSymbol()}
                    placeholder="Add symbol"
                    className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    maxLength="10"
                  />
                  <button
                    onClick={handleAddSymbol}
                    disabled={loading || loadingMarketData}
                    className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>

              {symbols.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No symbols in this watchlist. Add symbols to track their prices.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('symbol')}
                        >
                          <div className="flex items-center">
                            Symbol
                            {getSortIcon('symbol')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('currentPrice')}
                        >
                          <div className="flex items-center">
                            Current Price
                            {getSortIcon('currentPrice')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('open')}
                        >
                          <div className="flex items-center">
                            Open
                            {getSortIcon('open')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('high')}
                        >
                          <div className="flex items-center">
                            High
                            {getSortIcon('high')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('low')}
                        >
                          <div className="flex items-center">
                            Low
                            {getSortIcon('low')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('close')}
                        >
                          <div className="flex items-center">
                            Close
                            {getSortIcon('close')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('change')}
                        >
                          <div className="flex items-center">
                            Change
                            {getSortIcon('change')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('changePercent')}
                        >
                          <div className="flex items-center">
                            Change %
                            {getSortIcon('changePercent')}
                          </div>
                        </th>
                        <th 
                          className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('volume')}
                        >
                          <div className="flex items-center">
                            Volume
                            {getSortIcon('volume')}
                          </div>
                        </th>
                        <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedData.map((item) => {
                        const { symbol, marketInfo, hasError, change, changePercent } = item;
                        const companyName = assetNames[String(symbol || '').toUpperCase()] || '';
                        const symbolTitle = companyName ? `${symbol} — ${companyName}` : symbol;

                        if (hasError) {
                          return (
                            <tr key={symbol} className="bg-yellow-50">
                              <td className="px-2 py-1.5">
                                <div className="inline-flex items-center gap-1.5 flex-wrap">
                                  <button
                                    onClick={() => openSymbol(symbol)}
                                    className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-xs"
                                    title={symbolTitle}
                                    aria-label={symbolTitle}
                                  >
                                    {symbol}
                                  </button>
                                  <EarningsBadge symbol={symbol} />
                                  <NoteBadge symbol={symbol} />
                                </div>
                              </td>
                              <td colSpan="8" className="px-2 py-1.5 text-yellow-800 italic text-xs">
                                Error: {hasError}
                              </td>
                              <td className="px-2 py-1.5">
                                <button
                                  onClick={() => handleRemoveSymbol(symbol)}
                                  className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] font-medium"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        if (!marketInfo) {
                          return (
                            <tr key={symbol} className="bg-gray-50">
                              <td className="px-2 py-1.5">
                                <div className="inline-flex items-center gap-1.5 flex-wrap">
                                  <button
                                    onClick={() => openSymbol(symbol)}
                                    className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-xs"
                                    title={symbolTitle}
                                    aria-label={symbolTitle}
                                  >
                                    {symbol}
                                  </button>
                                  <EarningsBadge symbol={symbol} />
                                  <NoteBadge symbol={symbol} />
                                </div>
                              </td>
                              <td colSpan="8" className="px-2 py-1.5 text-gray-500 italic text-xs">
                                Loading...
                              </td>
                              <td className="px-2 py-1.5">
                                <button
                                  onClick={() => handleRemoveSymbol(symbol)}
                                  className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] font-medium"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        }

                        // change and changePercent are already calculated in sortableData
                        return (
                          <tr key={symbol} className="hover:bg-gray-50 transition-colors">
                            <td className="px-2 py-1.5">
                              <div className="inline-flex items-center gap-1.5 flex-wrap">
                                <button
                                  onClick={() => openSymbol(symbol, marketInfo)}
                                  className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-xs"
                                  title={symbolTitle}
                                  aria-label={symbolTitle}
                                >
                                  {symbol}
                                </button>
                                <EarningsBadge symbol={symbol} />
                                <NoteBadge symbol={symbol} />
                              </div>
                            </td>
                            <td className={`px-2 py-1.5 font-semibold text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(marketInfo.currentPrice)}
                            </td>
                            <td className="px-2 py-1.5 text-gray-700 text-xs">{formatCurrency(marketInfo.open)}</td>
                            <td className="px-2 py-1.5 text-green-600 font-medium text-xs">{formatCurrency(marketInfo.high)}</td>
                            <td className="px-2 py-1.5 text-red-600 font-medium text-xs">{formatCurrency(marketInfo.low)}</td>
                            <td className="px-2 py-1.5 text-gray-700 text-xs">{formatCurrency(marketInfo.close)}</td>
                            <td className={`px-2 py-1.5 font-medium text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {change !== null ? formatCurrency(change) : 'N/A'}
                            </td>
                            <td className={`px-2 py-1.5 font-medium text-xs ${changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {changePercent !== null ? formatPercent(changePercent) : 'N/A'}
                            </td>
                            <td className="px-2 py-1.5 text-gray-700 text-xs">{formatNumber(marketInfo.volume)}</td>
                            <td className="px-2 py-1.5">
                              <button
                                onClick={() => handleRemoveSymbol(symbol)}
                                className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 text-[10px] font-medium transition-colors"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchlistAlpaca;

